import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { TRANSACTION_REQUISITES_TAB } from 'components/constants';
import countries from 'components/countries';
import i18nContext from 'components/i18n-context';
import { validateIbanRequest } from 'services/requestAgent';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';

export const getRequisitesKindOptions = (i18n) => [
  {
    key: TRANSACTION_REQUISITES_TAB.IBAN,
    title: i18n.getMessage('createTransaction.payment.ibanTab.label')
  },
  {
    key: TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT,
    title: i18n.getMessage('createTransaction.payment.localBankAccount.label')
  },
  {
    key: TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES,
    title: i18n.getMessage('createTransaction.payment.bankAccount.label')
  }
];

// The bank account of the kind chosen in the section header with the bank details. The IBAN and the account have
// separate bank details, so switching the kind does not mix them. The account bank details are required for a non IBAN
// account only. An IBAN defines its bank: the IBAN is checked when its field is left after a change, and its bank
// details are replaced by the bank data of the check (a rejected IBAN or a failed check has none - a failed check is
// repeated when the field is left again). The bank country is the one of the check only. The bank name and BIC can be
// corrected after the check, the correction is kept until the IBAN is changed.
const RequisitesSection = ({
  values,
  errors,
  partyLabel,
  isCheckingIban,
  handleChange,
  setFieldValue,
  validateField,
  onIbanCheckingChange
}) => {
  const i18n = useContext(i18nContext);
  const [ibanCheckError, setIbanCheckError] = useState(null);
  const [isIbanChanged, setIsIbanChanged] = useState(false);

  const countriesOptions = Object.values(countries).map((value) => ({
    key: value,
    value: i18n.getMessage(value)
  }));

  const setIbanBank = async ({ bankCountry, bankName, bic } = {}) => {
    await setFieldValue('ibanBankCountry', bankCountry || null);
    await setFieldValue('ibanBankName', bankName || '');
    await setFieldValue('ibanBic', bic || '');
  };

  const handleIbanChange = (e) => {
    setIbanCheckError(null);
    setIsIbanChanged(true);
    handleChange(e);
  };

  // The bank country of the IBAN drives the commission and the payment purposes
  const handleIbanBlur = async (e) => {
    const iban = e.target.value?.trim();

    validateField('iban');

    if (!isIbanChanged || !iban) {
      return;
    }

    setIsIbanChanged(false);
    onIbanCheckingChange(true);

    try {
      await setIbanBank(await validateIbanRequest(iban));
    } catch (error) {
      await setIbanBank();

      if (error?.status === 400) {
        setIbanCheckError(i18n.getMessage('createTransaction.repeat.validation.invalidIban'));
      } else {
        setIsIbanChanged(true);
        setIbanCheckError(i18n.getMessage('createTransaction.repeat.validation.ibanCheckFailed'));
      }
    } finally {
      onIbanCheckingChange(false);
    }
  };

  const renderBankCountry = (name, { isRequired, isDisabled } = {}) => (
    <InputDropDownSearch
      isRequired={isRequired}
      isDisabled={isDisabled}
      className={'repeat-transaction-search-select'}
      label={i18n.getMessage('createTransaction.payment.bankCountry')}
      id={name}
      name={name}
      options={countriesOptions}
      value={values[name]}
      onChange={setFieldValue}
      onBlur={() => validateField(name)}
      error={errors?.[name]}
    />
  );

  const renderBankName = (name, isRequired) => (
    <Input
      isRequired={isRequired}
      className={'repeat-transaction-input'}
      label={i18n.getMessage('createTransaction.payment.bankName')}
      id={name}
      name={name}
      value={values[name]}
      onChange={handleChange}
      onBlur={() => validateField(name)}
      error={errors?.[name]}
    />
  );

  const renderBic = (name, isRequired) => (
    <Input
      isRequired={isRequired}
      className={'repeat-transaction-input'}
      label={i18n.getMessage('createTransaction.payment.bic')}
      id={name}
      name={name}
      value={values[name]}
      onChange={handleChange}
      onBlur={() => validateField(name)}
      error={errors?.[name]}
    />
  );

  return (
    <>
      {values.activePartyTab === TRANSACTION_REQUISITES_TAB.IBAN && (
        <>
          <div className={'repeat-transaction-row-block'}>
            <Input
              isRequired
              className={'repeat-transaction-input'}
              label={i18n.getMessage('createTransaction.payment.partyIban.label', { partyLabel })}
              placeholder={i18n.getMessage('createTransaction.payment.partyIban.placeholder')}
              id={'iban'}
              name={'iban'}
              value={values.iban}
              onChange={handleIbanChange}
              onBlur={handleIbanBlur}
              error={errors?.iban || ibanCheckError}
              isDisabled={isCheckingIban}
            />
            {renderBankCountry('ibanBankCountry', { isDisabled: true })}
          </div>
          <div className={'repeat-transaction-row-block'}>
            {renderBankName('ibanBankName', false)}
            {renderBic('ibanBic', false)}
          </div>
        </>
      )}

      {values.activePartyTab === TRANSACTION_REQUISITES_TAB.LOCAL_BANK_ACCOUNT && (
        <>
          <div className={'repeat-transaction-row-block'}>
            <Input
              isRequired
              className={'repeat-transaction-input'}
              label={i18n.getMessage('createTransaction.payment.accountNumber')}
              id={'accountNumber'}
              name={'accountNumber'}
              value={values.accountNumber}
              onChange={handleChange}
              onBlur={() => validateField('accountNumber')}
              error={errors?.accountNumber}
            />
            <Input
              isRequired
              className={'repeat-transaction-input'}
              label={i18n.getMessage('createTransaction.payment.sortCode')}
              id={'sortCode'}
              name={'sortCode'}
              value={values.sortCode}
              onChange={handleChange}
              onBlur={() => validateField('sortCode')}
              error={errors?.sortCode}
            />
          </div>
          <div className={'repeat-transaction-row-block'}>
            {renderBankCountry('bankCountry')}
            {renderBankName('bankName', false)}
            {renderBic('bic', false)}
          </div>
        </>
      )}

      {values.activePartyTab === TRANSACTION_REQUISITES_TAB.NON_IBAN_BANK_COUNTRIES && (
        <>
          <div className={'repeat-transaction-row-block'}>
            {renderBankCountry('bankCountry', { isRequired: true })}
            {renderBankName('bankName', true)}
          </div>
          <div className={'repeat-transaction-row-block'}>
            <Input
              isRequired
              className={'repeat-transaction-input'}
              label={i18n.getMessage('createTransaction.payment.accountNumber')}
              id={'accountNumber'}
              name={'accountNumber'}
              value={values.accountNumber}
              onChange={handleChange}
              onBlur={() => validateField('accountNumber')}
              error={errors?.accountNumber}
            />
            {renderBic('bic', true)}
          </div>
        </>
      )}
    </>
  );
};

RequisitesSection.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  partyLabel: PropTypes.string,
  // The IBAN check is controlled by the form - the form is not sent while it is in progress
  isCheckingIban: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  onIbanCheckingChange: PropTypes.func.isRequired
};

export default RequisitesSection;
